import { renderQr } from "../qr";
import type { RegistrationResponse } from "../types";

const formMarkup = `
  <main class="shell public-shell">
    <section class="brand-panel" aria-label="Nutrified Pet Expo 2026">
      <a class="brand-link" href="/" aria-label="Nutrified — strona główna">
        <img class="brand-logo" src="/nutrified-logo.png" alt="Nutrified" />
      </a>
      <div class="event-copy">
        <p class="eyebrow">PETEXPO 2026 · STOISKO A8</p>
        <h1>Świętujemy <em>5 lat</em><br />czystej radości!</h1>
        <p>Krzyżówka rozwiązana? Wpisz hasło i odbierz swój kod na stanowisku Nutrified.</p>
      </div>

      <label class="answer-panel" for="password">
        <span class="answer-panel-label">HASŁO Z KRZYŻÓWKI</span>
        <div class="answer-cells" aria-hidden="true">
          ${Array.from({ length: 12 }, (_, index) => `<span><b></b><small>${index + 1}</small></span>`).join("")}
        </div>
        <input class="answer-input" id="password" name="password" form="registration-form" type="text" autocomplete="off" autocapitalize="characters" maxlength="32" spellcheck="false" aria-label="Hasło z krzyżówki" aria-describedby="answer-hint" required />
        <span class="answer-hint" id="answer-hint">Dotknij pól i wpisz dwa słowa. Spacje są opcjonalne.</span>
      </label>

      <ol class="instruction-list" aria-label="Jak odebrać kod">
        <li><span>1</span><strong>Podaj hasło</strong></li>
        <li><span>2</span><strong>Wpisz dane</strong></li>
        <li><span>3</span><strong>Odbierz kod</strong></li>
      </ol>

      <div class="crossword-art" aria-hidden="true">
        <i></i><i></i><i class="filled"></i><i></i><i></i><i></i><i class="filled"></i><i></i><i></i><i></i><i></i>
      </div>

      <p class="booth-note">Nutrified — natura, wiedza i troska o zwierzęta.</p>
    </section>

    <section class="form-panel">
      <div class="form-wrap" id="registration-step">
        <p class="step-label">PRAWIE GOTOWE</p>
        <h2>Uzupełnij dane</h2>
        <p class="intro">Hasło wpisujesz w ponumerowane pola powyżej. Teraz zostaw nam kontakt.</p>

        <form id="registration-form" novalidate>
          <div class="field">
            <label for="name">Imię i nazwisko</label>
            <input id="name" name="name" autocomplete="name" maxlength="100" placeholder="Jan Kowalski" required />
          </div>

          <div class="field-grid">
            <div class="field">
              <label for="email">Adres e-mail</label>
              <input id="email" name="email" type="email" autocomplete="email" maxlength="254" placeholder="jan@example.com" required />
            </div>
            <div class="field">
              <label for="phone">Numer telefonu</label>
              <input id="phone" name="phone" type="tel" autocomplete="tel" maxlength="24" placeholder="+48 500 000 000" required />
            </div>
          </div>

          <label class="consent-row" for="marketingConsent">
            <input id="marketingConsent" name="marketingConsent" type="checkbox" />
            <span class="custom-check" aria-hidden="true"></span>
            <span>Chcę otrzymywać od Nutrified wiadomości o produktach, poradach i ofertach. Zgodę mogę wycofać w każdej chwili.</span>
          </label>

          <div class="form-error" id="form-error" role="alert" hidden></div>
          <button class="primary-button" type="submit">
            <span>Odbierz mój kod</span>
            <span aria-hidden="true">→</span>
          </button>
          <p class="privacy-note">Dane wykorzystamy do obsługi tej akcji, a marketingowo tylko po wyrażeniu zgody.</p>
        </form>
      </div>

      <div class="success-wrap" id="success-step" tabindex="-1" hidden>
        <div class="success-icon" aria-hidden="true">✓</div>
        <p class="eyebrow">CZYSTA RADOŚĆ!</p>
        <h2>Kod jest Twój,<br /><span id="success-name"></span>.</h2>
        <p class="intro">Pokaż go obsłudze stoiska A8. Zapisaliśmy Twoje zgłoszenie.</p>
        <div class="qr-card">
          <canvas id="registration-qr" aria-label="Kod QR rejestracji"></canvas>
          <p id="registration-id"></p>
        </div>
        <button class="secondary-button" id="new-registration" type="button">Dodaj kolejną osobę</button>
      </div>
      <p class="campaign-terms">Akcja trwa podczas PetExpo 25–27.08.2026 lub do wyczerpania puli nagród. Jedna nagroda na osobę.</p>
    </section>
  </main>
`;

export function renderRegistration(root: HTMLElement) {
  document.title = "Nutrified × Pet Expo 2026";
  root.innerHTML = formMarkup;

  const form = root.querySelector<HTMLFormElement>("#registration-form")!;
  const error = root.querySelector<HTMLDivElement>("#form-error")!;
  const submit = form.querySelector<HTMLButtonElement>("button[type='submit']")!;
  const password = root.querySelector<HTMLInputElement>("#password")!;
  const answerCells = [...root.querySelectorAll<HTMLElement>(".answer-cells span")];

  const updateAnswerCells = () => {
    const letters = password.value.normalize("NFC").replace(/\s/g, "").toLocaleUpperCase("pl").slice(0, 12);
    answerCells.forEach((cell, index) => {
      const letter = letters[index] ?? "";
      cell.querySelector<HTMLElement>("b")!.textContent = letter;
      cell.classList.toggle("has-letter", Boolean(letter));
    });
  };

  password.addEventListener("input", updateAnswerCells);

  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    error.hidden = true;

    if (!form.reportValidity()) return;

    const data = new FormData(form);
    submit.disabled = true;
    submit.classList.add("loading");
    submit.querySelector("span")!.textContent = "Rejestrujemy…";

    try {
      const response = await fetch("/api/registrations", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: String(data.get("name") ?? ""),
          email: String(data.get("email") ?? ""),
          phone: String(data.get("phone") ?? ""),
          password: String(data.get("password") ?? ""),
          marketingConsent: data.get("marketingConsent") === "on",
        }),
      });

      const payload = (await response.json()) as RegistrationResponse & { error?: string };
      if (!response.ok) throw new Error(payload.error || "Nie udało się zapisać zgłoszenia.");

      const registrationStep = root.querySelector<HTMLElement>("#registration-step")!;
      const successStep = root.querySelector<HTMLElement>("#success-step")!;
      const canvas = root.querySelector<HTMLCanvasElement>("#registration-qr")!;

      root.querySelector<HTMLElement>("#success-name")!.textContent = payload.registration.name.split(" ")[0];
      root.querySelector<HTMLElement>("#registration-id")!.textContent = `ID: ${payload.registration.id}`;
      await renderQr(canvas, payload.registration);
      registrationStep.hidden = true;
      successStep.hidden = false;
      successStep.focus({ preventScroll: true });
      if (window.matchMedia("(max-width: 979px)").matches) {
        successStep.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    } catch (caught) {
      error.textContent = caught instanceof Error ? caught.message : "Nie udało się zapisać zgłoszenia.";
      error.hidden = false;
    } finally {
      submit.disabled = false;
      submit.classList.remove("loading");
      submit.querySelector("span")!.textContent = "Odbierz mój kod";
    }
  });

  root.querySelector<HTMLButtonElement>("#new-registration")!.addEventListener("click", () => {
    form.reset();
    updateAnswerCells();
    root.querySelector<HTMLElement>("#success-step")!.hidden = true;
    root.querySelector<HTMLElement>("#registration-step")!.hidden = false;
    root.querySelector<HTMLInputElement>("#name")!.focus();
  });
}
